import type * as React from 'react';

import { CollisionPriority } from '@dnd-kit/abstract';
import { useSortable } from '@dnd-kit/react/sortable';
import { useTranslation } from 'react-i18next';

import { cn, tw } from '@crm/shared/lib';

import { ColumnHeader } from './ColumnHeader';
import columnPlaceholderImg from '../../assets/img/column-placeholder.png';
import {
    BOARD_COLUMN_WIDTH_CLASS_NAME,
    DEFAULT_COLUMN_COLOR,
    FALLBACK_COLUMN_BG_COLOR,
    FALLBACK_COLUMN_COLOR,
} from '../../config/boardColumnStyles';
import {
    boardColumnSortableModifiers,
    boardColumnSortablePlugins,
} from '../../model/dnd/boardColumnDnd.config';
import { useEditColumn, useDeleteColumn } from '../../model/edit-column';
import { EditColumn } from '../EditColumn';

import type { TDealColumn, TDealColumnColor } from '@crm/entities/deals-board';

interface IBoardColumn {
    headerText: string;
    columnId: TDealColumn['id'];
    columnColors: TDealColumnColor[];
    index: number;
    isDefault: boolean;
    isEmpty: boolean;
    main?: React.ReactNode;
    footer?: React.ReactNode;
    bgColor?: TDealColumn['bgColor'];
    color?: TDealColumn['color'];
    showPlaceholder?: boolean;
}

export const BoardColumn = ({
    headerText,
    columnId,
    columnColors,
    index,
    isDefault,
    isEmpty,
    main,
    footer,
    bgColor,
    color,
    showPlaceholder = false,
}: IBoardColumn) => {
    const { t } = useTranslation();
    const {
        isEditMode,
        isEditPending,
        previewColumnColor,
        previewColumnBgColor,
        closeEditMode,
        openEditMode,
        changePreviewColumnColor,
        handleEditColumnSubmit,
    } = useEditColumn(columnId);
    const { onDeleteColumn } = useDeleteColumn({ columnId, isEmpty });

    const boardColumnBaseStyles = tw(
        'relative flex min-h-0 cursor-default flex-col items-start gap-4.5 self-stretch overflow-x-hidden rounded-[10px] px-2 py-3.5 '
    );

    const boardColumnPlaceholderStyles = tw(
        'data-[dnd-placeholder=clone]:border-t-0 data-[dnd-placeholder=clone]:bg-black/[0.02] data-[dnd-placeholder=clone]:*:invisible'
    );
    const columnColor = color || (isDefault ? DEFAULT_COLUMN_COLOR : FALLBACK_COLUMN_COLOR);
    const visibleColumnColor = previewColumnColor ?? columnColor;
    const columnBgColor = bgColor || FALLBACK_COLUMN_BG_COLOR;
    const visibleColumnBgColor = previewColumnBgColor ?? columnBgColor;

    const { ref, handleRef, isDragging } = useSortable({
        id: `deal-column-${columnId}`,
        index,
        disabled: isDefault || isEditMode,
        type: 'deal-column',
        accept: ['deal', 'deal-column'],
        group: 'deals-board-columns',
        data: { columnId: String(columnId) },
        collisionPriority: CollisionPriority.Low,
        modifiers: boardColumnSortableModifiers,
        plugins: boardColumnSortablePlugins,
    });

    return (
        <div
            ref={ref}
            className={cn(
                boardColumnBaseStyles,
                BOARD_COLUMN_WIDTH_CLASS_NAME,
                boardColumnPlaceholderStyles,
                isDragging && 'cursor-grabbing'
            )}
            id={String(columnId)}
            style={{
                backgroundColor: visibleColumnBgColor,
                borderTop: `3px solid ${visibleColumnColor}`,
            }}
        >
            {isEditMode && (
                <EditColumn
                    columnColors={columnColors}
                    defaultColor={columnColor}
                    defaultTitle={headerText}
                    disabled={isEditPending}
                    skipSubmitWhenUnchanged
                    onClose={closeEditMode}
                    onColorPreviewChange={changePreviewColumnColor}
                    onSubmit={handleEditColumnSubmit}
                />
            )}

            {!isEditMode && (
                <ColumnHeader
                    isDefault={isDefault}
                    isDragging={isDragging}
                    headerText={headerText}
                    handleRef={handleRef}
                    visibleColumnColor={visibleColumnColor}
                    onDelete={onDeleteColumn}
                    onEditMode={openEditMode}
                />
            )}
            <div className="relative min-h-0 w-full flex-1">
                {showPlaceholder && (
                    <div
                        className={cn(
                            'pointer-events-none absolute inset-0 flex h-full -translate-y-16 flex-col items-center justify-center'
                        )}
                    >
                        <img
                            src={columnPlaceholderImg}
                            alt={t('crm.deals.board.column.emptyPlaceholderAlt')}
                        />
                        <span className="w-37.5 text-center text-xs text-smtr-main-tertiary-50">
                            {t('crm.deals.board.column.emptyDropHint')}
                        </span>
                    </div>
                )}
                {main ?? (
                    <div
                        className="h-full w-full bg-contain bg-center bg-no-repeat"
                        style={{
                            backgroundImage: `url(${columnPlaceholderImg})`,
                            backgroundPosition: 'center center',
                        }}
                    />
                )}
                {main && (
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8"
                        style={{
                            backgroundImage: `linear-gradient(180deg, transparent 0%, ${visibleColumnBgColor} 100%)`,
                        }}
                    />
                )}
            </div>

            {footer}
        </div>
    );
};
